/**
 * The inverse of the day geometry: pixels a finger moved, back into minutes.
 *
 * Its own module rather than a change to calendarEventLayout on purpose. That
 * one is a pure forward projection, and the overlap lanes are computed from
 * it — putting a gesture concern inside it would make the lane assignment
 * depend on what is currently being dragged.
 *
 * Pure: no React, no gesture library, no pixels beyond the ones handed in.
 * Everything a drag needs to decide is decided here, and tested here, before
 * any gesture is wired up.
 */
import type { LocalDate } from './calendarDate';
import { MIN_VISIBLE_MINUTES, type TimeWindow } from './calendarDayGeometry';

/**
 * A finger is not precise to the minute, and a meeting at 10:07 is noise
 * rather than intent.
 */
export const DRAG_SNAP_MINUTES = 15;

function snap(minutes: number): number {
  const snapped = Math.round(minutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
  // Normalise -0: a small upward drag would otherwise return -0, which is not
  // Object.is-equal to 0 and would make a "did anything move?" check lie.
  return snapped === 0 ? 0 : snapped;
}

export function pixelDeltaToMinutes(
  deltaY: number,
  window: TimeWindow,
  gridHeight: number,
): number {
  if (gridHeight <= 0) return 0;

  const windowSpan = window.endMinutes - window.startMinutes;
  return snap((deltaY / gridHeight) * windowSpan);
}

/**
 * Moving a meeting: both ends travel together.
 *
 * Clamping preserves the DURATION rather than truncating it — dragging a
 * one-hour meeting off the top of the window must not silently turn it into a
 * fifteen-minute one.
 */
export function resolveDraggedSpan(
  span: TimeWindow,
  deltaMinutes: number,
  window: TimeWindow,
): TimeWindow {
  if (deltaMinutes === 0) return span;

  const duration = span.endMinutes - span.startMinutes;
  const maxStart = window.endMinutes - duration;

  const startMinutes = Math.min(
    Math.max(span.startMinutes + deltaMinutes, window.startMinutes),
    maxStart,
  );

  return { startMinutes, endMinutes: startMinutes + duration };
}

/**
 * Resizing a meeting: only the end moves.
 *
 * The floor is MIN_VISIBLE_MINUTES, the shortest span the grid can draw — and
 * the row height is solved so that span clears the 44px touch target, so
 * anything shorter would render a block a finger cannot hit.
 */
export function resolveResizedSpan(
  span: TimeWindow,
  deltaMinutes: number,
  window: TimeWindow,
): TimeWindow {
  if (deltaMinutes === 0) return span;

  const endMinutes = Math.min(
    Math.max(span.endMinutes + deltaMinutes, span.startMinutes + MIN_VISIBLE_MINUTES),
    window.endMinutes,
  );

  return { startMinutes: span.startMinutes, endMinutes };
}

/**
 * A dropped span, back into the instants the API stores.
 *
 * The grid speaks in minutes since local midnight of the day the block sits
 * on; `startsAt`/`endsAt` are UTC instants. Resolving that with the DEVICE's
 * offset — `new Date(...).setHours(0,0,0,0)` — would shift every dragged
 * meeting whenever the viewer's zone differs from the event's, which is the
 * normal case for a distributed team. So the offset is measured in the target
 * zone instead.
 */
export function spanToInstants(
  date: LocalDate,
  span: TimeWindow,
  timeZone: string,
): { startsAt: string; endsAt: string } {
  const midnightUtc = Date.UTC(date.year, date.month - 1, date.day);
  // What UTC instant is local midnight in `timeZone`? Probe the zone's offset
  // at that moment and subtract it.
  const offsetMinutes = zoneOffsetMinutes(midnightUtc, timeZone);
  const localMidnight = midnightUtc - offsetMinutes * 60_000;

  return {
    startsAt: new Date(localMidnight + span.startMinutes * 60_000).toISOString(),
    endsAt: new Date(localMidnight + span.endMinutes * 60_000).toISOString(),
  };
}

function zoneOffsetMinutes(utcMillis: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(utcMillis));
  const at = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  const asUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour'),
    at('minute'),
    at('second'),
  );
  return (asUtc - utcMillis) / 60_000;
}
